import BoundedRangePicker from '@/components/bounded-range-picker';
import TimestampRangePicker from '@/components/timestamp-RangePicker';
import { Button, Col, Drawer, Form, Input, message, Row, Select, Space, Modal } from '@drip/drip-design';
import moment from 'moment';
import React, { useState, useImperativeHandle, useEffect, useMemo } from 'react';
import Calendar from './calendar';
import apis from './apis';
import { cloneDeep, keysIn } from 'lodash';
import { filterConditions, thousands } from '@/common/utils';
import Chart from '../charts';
import './index.less';
import ComponentLog from '@/components/component-log';
import TypeSelect from './components/type-select';
import queryTree from '@/apis';
import { useQueryList } from '@/pages/product-manager/sale-plan/components/sales-plan-drawer/calendar/add-plan/normal-select.js';
import { goto } from '@/common/goto';

// type statusType = 'new' | 'edit' | 'view';

function SalesPlanDrawer(props, ref) {
  const { visible, onClose, onDownLoad } = props;
  const [drawerStatus, setDrawerStatus] = useState('new');
  const [marketingPlanCode, setMarketingPlanCode] = useState('');
  const [form] = Form.useForm();
  const [planInfo, setPlanInfo] = useState({ loading: false });

  useImperativeHandle(ref, () => {
    return {
      getOpenStatus: ({ status, id }) => {
        setDrawerStatus(status);
        setMarketingPlanCode(id);
      },
    };
  });

  useEffect(() => {
    if (!visible) {
      setPlanInfo({ loading: false });
      setMarketingPlanCode('');
    }
  }, [visible, form]);

  useEffect(() => {
    if (!marketingPlanCode) return;
    setPlanInfo((info) => Object.assign({}, info, { loading: true }));
    apis
      .marketingPlanDetail({ marketingPlanCode })
      .then((res) => {
        const { ret, msg, data } = res;
        if (ret !== '0' || !data) throw msg || '计划请求失败';
        setPlanInfo((info) => Object.assign({}, info, data, { loading: false }));
      })
      .catch((ex) => {
        message.error(ex);
        setPlanInfo((info) => Object.assign({}, { loading: false }));
      });
  }, [marketingPlanCode]);

  useEffect(() => {
    if (!form) return;
    if (drawerStatus == 'new') {
      form.resetFields();
      setSaveDraftDisabled(true);
      setAddPlanDisabled(true);
      return;
    }
    if (!planInfo.marketingPlanCode) return;
    const { name, startTime, endTime, type, levelPlan } = { ...planInfo };
    let time;
    if (startTime && endTime) {
      time = [moment(startTime), moment(endTime)];
    }
    setSaveDraftDisabled(!name);
    setAddPlanDisabled(!name || !time || !levelPlan || !levelPlan.length);
    form.setFieldsValue({ name, time, type, levelPlan });
  }, [planInfo, form, drawerStatus]);

  function getTitle() {
    switch (drawerStatus) {
      case 'new':
        return '编辑销售计划';
      case 'edit':
        return '编辑销售计划';
      case 'view':
        return '销售计划';
      default:
        return '新建销售计划';
    }
  }

  const savePlan = (status) => {
    const value = cloneDeep(form.getFieldsValue(true));
    if (value.time) {
      value.startTime = getDayStart(value.time[0]);
      value.endTime = getDayEnd(value.time[1]);
      delete value.time;
    }

    const save = drawerStatus === 'edit' ? apis.marketingPlanUpdate : apis.marketingPlanCreate;

    save(filterConditions({ status: status, ...value, marketingPlanCode }))
      .then((res) => {
        const { ret, msg } = res;
        if (ret !== '0') throw msg || '保存计划失败';
        message.success('计划保存成功');
        onClose();
      })
      .catch((ex) => {
        message.error(ex);
      });
  };

  const onDrawerClose = () => {
    if (drawerStatus == 'view') {
      onClose();
      return;
    }
    const formValue = form?.getFieldsValue(true);
    if (drawerStatus == 'new') {
      const keys = Object.keys(formValue);
      const hasValue = keys.find((ele) => !!formValue[ele]);
      if (!keys.length || !hasValue) {
        onClose();
        return;
      }
    }
    Modal.confirm({
      title: '信息暂未保存，是否存为草稿',
      okText: '存为草稿',
      cancelText: '直接退出',
      onCancel: onClose,
      centered: true,
      onOk: () => savePlan(0),
    });
  };

  const [saveDraftDisabled, setSaveDraftDisabled] = useState(true);
  const [addPlanDisabled, setAddPlanDisabled] = useState(true);

  const onValuesChange = (val, allVal) => {
    setSaveDraftDisabled(!allVal.name);
    setAddPlanDisabled(!allVal.name || !allVal.time || !allVal.levelPlan || !allVal.levelPlan.length);
  };

  // 校验erp下货品
  const [type, setType] = useState(1);
  const [option, setOption] = useState([]);

  const queryOption = (query) => {
    if (query.type == 2) {
      apis
        .marketingPlanSelectorData(query)
        .then((res) => {
          const { ret, msg, data } = res || {};
          if (ret !== '0' || !data) throw msg || '网络异常请稍候~';

          setOption(data.list);
        })
        .catch((ex) => {
          message.error(ex);
        });
    }
  };

  useEffect(() => {
    queryOption({ type });
  }, [type]);

  const beforeJump = () => {
    const { name } = form?.getFieldsValue(true);
    if (name) {
      Modal.confirm({
        centered: true,
        title: '即将跳转，是否将该计划存为草稿？',
        okText: '存为草稿',
        cancelText: '直接跳转',
        onCancel: () => goto('/product-manager/product-group', 'newTab'),
        onOk: () => savePlan(0),
      });
    } else {
      goto('/product-manager/product-group', 'newTab');
    }
  };

  const editFooter = (
    <div style={{ textAlign: 'center' }}>
      <Space>
        <Button onClick={onDrawerClose}>取消</Button>
        <Button onClick={() => savePlan(0)} type="primary" disabled={saveDraftDisabled}>
          存为草稿
        </Button>
        <Button onClick={() => savePlan(1)} type="primary" disabled={addPlanDisabled}>
          完成
        </Button>
      </Space>
    </div>
  );
  const viewFooter = (
    <div style={{ textAlign: 'center' }}>
      <Space>
        <Button onClick={onClose}>关闭</Button>
        <ComponentLog clstag="yx_center_hzcp_1678258944439|26">
          <Button onClick={() => onDownLoad(marketingPlanCode)} type="primary">
            下载数据
          </Button>
        </ComponentLog>
      </Space>
    </div>
  );

  return (
    <Drawer
      title={getTitle()}
      width={1100}
      visible={visible}
      onClose={onDrawerClose}
      maskClosable={false}
      footer={drawerStatus === 'view' ? viewFooter : editFooter}
      destroyOnClose={true}
      className="edit-product-plan-drawer"
    >
      {drawerStatus === 'view' ? (
        <SalesPlanView planInfo={planInfo} />
      ) : (
        <>
          <div className="edit-plan-title">
            第一步：设定基础信息<span className="required">（必填）</span>
          </div>
          <Form
            form={form}
            layout="vertical"
            validateTrigger="onChange"
            preserve={false}
            onValuesChange={onValuesChange}
          >
            <Row>
              <Col span={7}>
                <Form.Item
                  label="计划名称"
                  name="name"
                  required
                  rules={[
                    { required: true, message: '请输入计划名称' },
                    { max: 20, message: '计划名称不超过20个字' },
                  ]}
                >
                  <Input
                    placeholder="示例：2023年第一季度日销计划"
                    onChange={(e) => {
                      setSaveDraftDisabled(!e.target.value);
                    }}
                  />
                </Form.Item>
              </Col>
              <Form.Item noStyle shouldUpdate={(prev, curr) => prev.levelPlan !== curr.levelPlan}>
                {({ getFieldValue }) => {
                  const levelPlan = getFieldValue('levelPlan');
                  return (
                    <>
                      <Col span={7} offset={1}>
                        <Form.Item
                          label="计划时间"
                          name="time"
                          required
                          rules={[{ required: true, message: '请选择计划时间' }]}
                        >
                          <RangePicker levelPlan={levelPlan} />
                        </Form.Item>
                      </Col>
                      <Col span={7} offset={1}>
                        <Form.Item
                          label="计划类型"
                          name="type"
                          required
                          rules={[{ required: true, message: '请选择计划类型' }]}
                          help={
                            type == 2 && !option.length ? (
                              <>
                                暂无可选择的商品组，若想依据商品分组设定计划，请先去
                                <a onClick={beforeJump}>商品分组管理页</a>
                                创建分组
                              </>
                            ) : null
                          }
                        >
                          <TypeSelect levelPlan={levelPlan} onChange={(val) => setType(val)} />
                        </Form.Item>
                      </Col>
                    </>
                  );
                }}
              </Form.Item>
            </Row>

            <div className="edit-plan-title">
              第二步：设定销售计划<span className="required">（必填）</span>
            </div>
            <Form.Item
              noStyle
              shouldUpdate={(prev, curr) =>
                prev.name !== curr.name || prev.time !== curr.time || prev.type !== curr.type
              }
            >
              {({ getFieldsValue }) => {
                const { time, name, type } = getFieldsValue(true);
                const disabled = !(time && time[0] && name && type) || (type == 2 && !option.length);
                return (
                  <Form.Item name="levelPlan" noStyle rules={[{ required: true, message: '请设置销售计划' }]}>
                    <Calendar
                      status="edit"
                      disabled={disabled}
                      startTime={time && time[0]}
                      endTime={time && time[1]}
                      type={type}
                    />
                  </Form.Item>
                );
              }}
            </Form.Item>
          </Form>
        </>
      )}
    </Drawer>
  );
}

function RangePicker({ value, onChange, levelPlan }) {
  const getTimeSpace = () => {
    if (!levelPlan || !levelPlan.length) return false;
    let timeRangeArrStart = [];
    let timeRangeArrEnd = [];
    levelPlan.map((ele) => {
      const { levelPlan } = ele;
      if (!levelPlan) return;
      levelPlan.sort((a, b) => a.startTime - b.startTime);
      timeRangeArrStart.push(levelPlan[0].startTime);
      timeRangeArrEnd.push(levelPlan[levelPlan.length - 1].endTime);
    });
    timeRangeArrStart.sort((a, b) => a - b);
    timeRangeArrEnd.sort((a, b) => b - a);
    return [timeRangeArrStart[0], timeRangeArrEnd[0]];
  };
  const onValueChange = (val) => {
    const timeSpace = getTimeSpace();
    const startTime = getDayStart(val[0]);
    const entTime = getDayEnd(val[1]);
    if (!timeSpace || (startTime <= timeSpace[0] && entTime >= timeSpace[1])) {
      onChange(val);
    } else {
      Modal.confirm({
        title: '需清空计划后才可修改',
        centered: true,
      });
    }
  };

  const disabledDate = (current) => {
    return current && current < moment().startOf('month');
  };

  return (
    <BoundedRangePicker
      value={value}
      onChange={onValueChange}
      spanTime={92}
      disabledDate={disabledDate}
      allowClear={false}
    />
  );
}

function getDayStart(val) {
  return moment(moment(val).format('YYYY-MM-DD 00:00:00')).valueOf();
}
function getDayEnd(val) {
  return moment(moment(val).format('YYYY-MM-DD 23:59:59')).valueOf();
}

function SalesPlanView({ planInfo }) {
  const { startTime, name, endTime, totalAmount, completeAmount, completeRate, levelPlan, type } = planInfo;
  return (
    <div className="planInfo-view-panel">
      <div className="planInfo-view-panel-name">{name}</div>
      <div className="planInfo-view-panel-time">
        计划时间：{moment(startTime).format('YYYY/MM/DD')} - {moment(endTime).format('YYYY/MM/DD')}
      </div>
      <div className="planInfo-view-panel-amount">
        <div className="planInfo-view-panel-amount-item">
          <div className="label">总目标成交金额</div>
          <div className="amount">￥{thousands(totalAmount)}</div>
        </div>
        <div className="planInfo-view-panel-amount-item">
          <div className="label">当前成交金额</div>
          <div className="amount">￥{thousands(completeAmount)}</div>
        </div>
        <div className="planInfo-view-panel-amount-item">
          <div className="label">达成率</div>
          <div className="amount">{completeRate}</div>
        </div>
      </div>
      <div className="chart-block">
        <Chart levelPlan={planInfo.levelPlan} />
      </div>
      <div>
        <Calendar status="view" startTime={startTime} endTime={endTime} value={levelPlan} type={type} />
      </div>
    </div>
  );
}

export default React.forwardRef(SalesPlanDrawer);
